import type {
  CreateUploadTasksArgs,
  UploadTaskLike,
  UploadTaskResult,
} from '@/hooks/uploads/createUploadTasks'

// Allow large files (up to 100 MB) on slower connections while ensuring a
// stalled request eventually settles and the upload UI can be retried.
const UPLOAD_TIMEOUT_MS = 10 * 60 * 1000

const uploadSingleFile = async ({
  uploadUrl,
  accessToken,
  fileUri,
  fileName,
  onProgress,
}: {
  uploadUrl: string
  accessToken: string
  fileUri: string
  fileName: string
  onProgress?: (
    totalBytesSent: number,
    totalBytesExpectedToSend: number
  ) => void
}): Promise<UploadTaskResult> => {
  // DocumentPicker / drag-and-drop on web produce blob: or data: URIs. We need
  // to fetch that URI to obtain a Blob we can put into FormData for a proper
  // multipart upload to the fastify endpoint.
  const response = await fetch(fileUri)
  if (!response.ok) {
    throw new Error(`Could not read upload file: HTTP ${response.status}`)
  }
  const blob = await response.blob()

  const formData = new FormData()
  // fastify's saveRequestFiles picks up `fileName` as a text field via
  // file.fields.fileName?.value — keep field order consistent with native.
  formData.append('fileName', fileName)
  formData.append('file', blob, fileName)

  return new Promise<UploadTaskResult>((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.timeout = UPLOAD_TIMEOUT_MS
    xhr.open('POST', uploadUrl)
    xhr.setRequestHeader('Authorization', `Bearer ${accessToken}`)

    let settled = false
    const cleanup = () => {
      settled = true
      xhr.onload = null
      xhr.onerror = null
      xhr.onabort = null
      xhr.ontimeout = null
      xhr.upload.onprogress = null
    }
    const fail = (error: unknown) => {
      if (settled) return
      cleanup()
      reject(error)
    }

    if (onProgress) {
      xhr.upload.onprogress = (event: ProgressEvent) => {
        if (!settled && event.lengthComputable) {
          onProgress(event.loaded, event.total)
        }
      }
    }

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        cleanup()
        resolve({
          body: xhr.responseText,
          status: xhr.status,
        })
      } else {
        fail(
          new Error(
            `Upload failed with status ${xhr.status}: ${xhr.responseText}`
          )
        )
      }
    }
    xhr.onerror = () => fail(new Error('Network error during upload'))
    xhr.onabort = () => fail(new Error('Upload aborted'))
    xhr.ontimeout = () => fail(new Error('Upload timed out. Please try again.'))

    try {
      xhr.send(formData)
    } catch (error) {
      fail(error)
    }
  })
}

const createUploadTasks = ({
  uploadUrl,
  accessToken,
  files,
  progressCallback,
}: CreateUploadTasksArgs): UploadTaskLike[] => {
  return files.map((file, index) => ({
    uploadAsync: () =>
      uploadSingleFile({
        uploadUrl,
        accessToken,
        fileUri: file.fileUri,
        fileName: file.fileName,
        onProgress: progressCallback
          ? (totalBytesSent, totalBytesExpectedToSend) =>
              progressCallback(
                {
                  totalBytesSent,
                  totalBytesExpectedToSend,
                },
                index
              )
          : undefined,
      }),
  }))
}

export default createUploadTasks
